import styled, { css } from 'styled-components'

import Button from 'components/Buttons/Button'
import Icon from 'components/Icon'
import Text from 'components/Text'
import TextInput from 'components/TextInput'

import { colors } from 'utils/colors'
import { TableVariant } from '.'

export const BulkOperationPanelContainer = styled.div`
  background-color: ${(p) => p.theme.colors.gray3};
  padding: 7px 16px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
`

export const SelectIconContainer = styled.div`
  cursor: pointer;
  display: inline-block;
  margin-right: 8px;
  vertical-align: middle;
`

export const SelectionLabel = styled((props) => <Text {...props} />)`
  ${(p) => p.theme.font({ weight: 'medium' })}
  color: ${(p) => p.theme.colors.gray8};
  display: inline-block;
  line-height: 24px;
  margin: 0 8px;
  max-width: 25%;
  overflow: hidden;
  text-transform: uppercase;
  text-overflow: ellipsis;
  vertical-align: middle;
  white-space: nowrap;
`

export const CaretDown = styled(Icon)`
  margin-top: -7px;
`

export const HeaderWrapper = styled.div`
  ${(p) => p.theme.font({ size: 14, weight: 'medium' })}
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  min-width: 0;
  width: 100%;
`

export const TitleContainer = styled.div`
  flex: 1 1 auto;
  min-width: 3ch;
  overflow: hidden;
`

export const SortContainer = styled.p`
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-left: 5px;
  flex-shrink: 0;

  & > svg {
    ${(p) => p.theme.font({ color: 'gray5', size: 14 })};
  }
`

export const PageButton = styled((p) => <Button {...p} />)<{ noBorderLeft?: boolean; noBorderRight?: boolean }>`
  ${(p) => p.theme.font({ color: 'gray7', size: 14, weight: 'medium' })};
  align-items: center;
  background-color: ${colors.white};
  border: 1px solid ${colors.gray4};
  border-radius: revert;
  display: flex;
  height: 32px;
  justify-content: center;
  padding: 0;

  & > svg {
    ${(p) => p.theme.font({ size: 14, weight: 'normal' })};
  }

  :disabled {
    border: 1px solid ${colors.gray4};
    background-color: ${colors.white};
    color: ${colors.gray4};

    svg {
      color: ${colors.gray4};
    }

    &.page-number {
      color: ${colors.gray7};
    }

    ${({ noBorderLeft }) => noBorderLeft && `border-left: none;`}
    ${({ noBorderRight }) => noBorderRight && `border-right: none;`}
  }

  ${({ noBorderLeft }) => noBorderLeft && `border-left: none;`}
  ${({ noBorderRight }) => noBorderRight && `border-right: none;`}
`

export const Pagination = styled.div<{ borderless?: boolean; variant?: TableVariant }>`
  box-sizing: border-box;
  background-color: ${({ borderless }) => (borderless ? 'transparent' : colors.gray1)};
  ${({ variant }) => variant === 'inline-table' && `background-color:${colors.white}`};
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  display: flex;
  height: 70px;
  justify-content: space-between;
  padding: 0 1rem;
`

export const PageNumber = styled.div`
  align-items: center;
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  overflow-x: auto;
`

export const PageSize = styled.div`
  align-items: center;
  display: flex;
`

export const PageInput = styled(TextInput)`
  background-color: ${colors.white};
  border: 1px solid ${colors.gray4};
  padding: 0 4px;
  text-align: center;
`

const CellCss = css`
  padding: 0.5rem 0.75rem;
  justify-content: center;

  &.center > div:first-child {
    justify-content: center;
  }

  &::after {
    content: '';
  }

  &.fixed::after {
    color: #2b2b2b;
    box-shadow: inset 5px 0 8px -8px;
    height: 100%;
    position: absolute;
    top: 0;
    right: -8px;
    width: 10px;
  }

  .resizer {
    background: #d8d8d8;
    display: inline-block;
    height: 100%;
    position: absolute;
    right: 1px;
    top: 0;
    touch-action: none;
    transform: translateX(50%);
    width: 1px;

    &.isResizing {
      background: ${colors.gray3};
    }
  }
`

export const StyledHeader = styled.div<{
  width?: number | string
  borderless?: boolean
  isCheckboxColumn?: boolean
  variant: TableVariant
}>`
  ${CellCss}
  ${(p) => p.theme.font({ color: 'gray5', size: 14, weight: 'medium' })};
  background: ${(p) => p.theme.colors.gray13};
  ${({ variant }) => variant === 'inline-table' && 'height:50px'};
  border-bottom: 1px solid ${(p) => p.theme.colors.gray3};
  ${(p) => p.isCheckboxColumn && `width: 50px;`}
  display: flex;
  flex-direction: column;
  padding: 8px 15px;
  user-select: none;
`

export const StyledRow = styled.div<{ height?: number }>`
  width: max-content;
  height: ${(p) => (p.height ? `${p.height}px` : 'calc(100% - 1px)')};
  border-bottom: 1px solid ${colors.gray3};

  &.row-even {
    background: ${colors.white};
  }

  &.row-odd {
    background: ${colors.gray1};
  }

  &.row-selected {
    cursor: pointer;
    background-color: ${colors.blue5};
  }

  :hover {
    background-color: ${colors.blue5};
  }

  ${(p) =>
    !!p.onClick &&
    css`
      :hover {
        cursor: pointer;
        background-color: ${colors.blue5};
      }
    `}
`

export const StyledCell = styled.div<{
  colored: boolean
  width?: number | string
  hasSelected?: boolean
  variant: TableVariant
}>`
  ${CellCss}
  ${(p) => p.theme.font({ color: 'gray7', size: 14 })};
  display: flex;
  flex-direction: column;
  ${({ variant }) => variant === 'inline-table' && `height:50px;justify-content:center`};
  padding: 8px 15px;
  ${(p) =>
    (p.colored || p.hasSelected) &&
    css`
      :first-child {
        border-left: ${p.colored ? `3px solid ${colors.blue1}` : 'none'};
        padding-left: calc(1rem - ${p.colored ? '10px' : '5px'});
      }
    `}
  ${(p) =>
    p.colored &&
    css`
      background-color: ${colors.blue5};
    `}

  &.highlight {
    background: ${(p) => p.theme.colors.blue5};
  }
`

export const StyledTable = styled.div<{ bodyHeight?: string; borderless: boolean; variant: TableVariant }>`
  box-sizing: border-box;
  border: 1px solid ${(p) => p.theme.colors.gray3};
  ${({ variant }) => variant === 'inline-table' && 'border-radius:4px'};
  ${(p) =>
    p.borderless &&
    `
      border-left: none;
      border-right: none;
    `}

  * > .fixed {
    position: sticky !important;
    left: 0;
    top: 0;
    z-index: 1;
  }

  .bulk-action {
    height: 0;
  }

  .clone-table {
    th,
    td {
      line-height: initial;
      padding: 0;
    }

    td {
      ${(p) => p.theme.font({ color: 'gray7', size: 14 })};
      height: 70px;
    }

    .draggable {
      cursor: move;
      user-select: none;
    }

    .dragging {
      background: ${colors.white};
      border-left: 1px solid ${colors.white};
      border-top: 1px solid ${colors.white};
    }
  }

  .placeholder {
    background-color: ${colors.gray5};
    border: 2px dashed ${colors.gray4};
  }

  .clone-list {
    display: flex;
  }
`
